import React, { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useCookies } from "react-cookie";
import {
  IP_NOT_ALLOWED_PATH,
  isIpNotAllowedState,
} from "@/utils/ipRestriction";
import { type ICookiesType } from "@/types/cookies";

/**
 * 許可IP外と判定された状態でも表示してよい画面
 *
 * メンテナンス画面を含めるのは、メンテナンスモード中は `/ip-not-allowed` の
 * ルートが定義されず全てのパスが `/maintenance` へ送られるため。含めないと
 * ガードとルート定義が押し合い、どちらの画面にも到達できなくなる。
 */
const ACCESSIBLE_PATHS = [IP_NOT_ALLOWED_PATH, "/login", "/maintenance"];

interface IIpRestrictionGuardProps {
  children: ReactNode;
}

/**
 * 許可IP外と判定された状態でのアプリ画面表示を遮断する
 *
 * ログイン状態を保持する仕様のため、ブラウザバックやブックマークからアプリ画面へ
 * 遷移できてしまう。APIは403になるものの画面自体は描画されるため、遷移の時点で止める。
 *
 * 遮断はログイン中のみ行う。拒否状態はlocalStorageに残るため、ブラウザを再起動すると
 * 未ログイン（認証クッキーはセッションクッキー）なのに記録だけが残る。この状態で遮断すると
 * 許可ネットワークに戻っていても案内ページから抜け出せなくなる。未ログインなら
 * アプリ画面側の認証チェックがログイン画面へ送るため、ここで止める必要はない。
 */
export const IpRestrictionGuard: React.FC<IIpRestrictionGuardProps> = ({
  children,
}) => {
  const location = useLocation();
  const [cookies] = useCookies();
  const isLoggedIn = (cookies as ICookiesType).isLogin === "true";

  if (
    isLoggedIn &&
    isIpNotAllowedState() &&
    !ACCESSIBLE_PATHS.includes(location.pathname)
  ) {
    return <Navigate replace to={IP_NOT_ALLOWED_PATH} />;
  }

  return <>{children}</>;
};
